import { useReducer, useCallback, useRef } from "react";
import moment from "moment";
import TripContext from "./context";
import TripReducer, { initialState } from "./reducer";
import {
  TRIP_LIST,
  TRIP_DETAILS,
  TRIP_QUERY,
  TRIP_LOADING,
  RESET_TRIP,
} from "./types";
import { ITripQuery } from "../../models/trip/query";
import {
  ITripRequest,
  ITripCreateRequest,
  IAddOrderToTripRequest,
  IRemoveOrderFromTripRequest,
  IExportTripFinancialReportRequest,
} from "../../models/trip/request";
import {
  IConfirmTripPaymentRequest,
  IConfirmTripPaymentResponse,
} from "../../models/trip/response";
import EndPoints from "../../services/end-points";
import { execute } from "../../utils/helpers/execute";

const TripContextProvider = ({ children }: any) => {
  const [state, dispatch] = useReducer(TripReducer, initialState);
  const detailsRequestIdRef = useRef(0);

  const addLoading = (key: string) => {
    dispatch({ type: TRIP_LOADING, payload: [...state.loading, key] });
  };

  const removeLoading = (key: string) => {
    dispatch({
      type: TRIP_LOADING,
      payload: state.loading.filter((item: string) => item !== key),
    });
  };

  const getList = useCallback(async () => {
    try {
      addLoading("list");
      const response = await EndPoints.trip.getAllTrips(state.query);
      dispatch({
        type: TRIP_LIST,
        payload: { data: response.data.data, totalCount: response.data.total },
      });
    } catch (error) {
      console.error(error);
    } finally {
      removeLoading("list");
    }
  }, [state.query]);

  const getDetails = useCallback(async (id: number) => {
    const requestId = ++detailsRequestIdRef.current;
    try {
      addLoading("details");
      const response = await EndPoints.trip.getTrip(id);
      if (requestId === detailsRequestIdRef.current) {
        dispatch({ type: TRIP_DETAILS, payload: response.data });
      }
    } catch (error) {
      console.error(error);
    } finally {
      if (requestId === detailsRequestIdRef.current) {
        removeLoading("details");
      }
    }
  }, []);

  const create = useCallback(
    async (data: ITripCreateRequest): Promise<boolean> => {
      try {
        addLoading("create");
        await EndPoints.trip.createTrip(data);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      } finally {
        removeLoading("create");
      }
    },
    [],
  );

  const update = useCallback(
    async (id: number, data: ITripRequest): Promise<boolean> => {
      try {
        addLoading("update");
        await EndPoints.trip.updateTrip(id, data);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      } finally {
        removeLoading("update");
      }
    },
    [],
  );

  const addOrderToTrip = useCallback(
    async (id: number, data: IAddOrderToTripRequest): Promise<boolean> => {
      try {
        addLoading("addOrder");
        await EndPoints.trip.addOrderToTrip(id, data);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      } finally {
        removeLoading("addOrder");
      }
    },
    [],
  );

  const removeOrderFromTrip = useCallback(
    async (id: number, data: IRemoveOrderFromTripRequest): Promise<boolean> => {
      try {
        addLoading("removeOrder");
        await EndPoints.trip.removeOrderFromTrip(id, data);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      } finally {
        removeLoading("removeOrder");
      }
    },
    [],
  );

  const setQuery = useCallback((query: ITripQuery) => {
    dispatch({ type: TRIP_QUERY, payload: query });
  }, []);

  const exportExcel = useCallback(
    async (request: any) => {
      await execute({
        callback: async () => {
          addLoading("create");
          const data = await EndPoints.trip.exportExcel(request, state.query);
          const url = window.URL.createObjectURL(new Blob([data.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `Trips-${moment().format("yyyy-MM-d")}.xlsx`,
          );
          document.body.appendChild(link);
          link.click();
        },
        fallback: (error) => {},
        finallyCallback: () => {
          removeLoading("create");
        },
        throwException: true,
      });
    },
    [state.query],
  );

  const exportFinancialReport = useCallback(
    async (request: IExportTripFinancialReportRequest) => {
      await execute({
        callback: async () => {
          addLoading("exportFinancialReport");
          const data = await EndPoints.trip.exportFinancialReport(request);
          const url = window.URL.createObjectURL(new Blob([data.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `trip-financial-report-${request.startDate}-to-${request.endDate}.xlsx`,
          );
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        },
        fallback: (error) => {},
        finallyCallback: () => {
          removeLoading("exportFinancialReport");
        },
        throwException: true,
      });
    },
    [],
  );

  const confirmPayment = useCallback(
    async (
      id: number,
      data: IConfirmTripPaymentRequest,
    ): Promise<IConfirmTripPaymentResponse | null> => {
      try {
        addLoading("confirmPayment");
        const response = await EndPoints.trip.confirmPayment(id, data);
        return response.data;
      } catch (error) {
        console.error(error);
        return null;
      } finally {
        removeLoading("confirmPayment");
      }
    },
    [],
  );

  const reset = useCallback(() => {
    dispatch({ type: RESET_TRIP });
  }, []);

  return (
    <TripContext.Provider
      value={{
        ...state,
        actions: {
          getList,
          getDetails,
          create,
          update,
          updateTrip: update,
          addOrderToTrip,
          removeOrderFromTrip,
          exportExcel,
          exportFinancialReport,
          confirmPayment,
          setQuery,
          reset,
        },
      }}
    >
      {children}
    </TripContext.Provider>
  );
};

export default TripContextProvider;
